/**
 * @fileoverview
 * Notification Store - Manages notification state using Signals
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, signal, computed } from '@angular/core';
import { 
  Notification, 
  NotificationState, 
  DEFAULT_NOTIFICATION_STATE,
  NotificationType,
  NotificationPosition 
} from '../models';

@Injectable({
  providedIn: 'root'
})
export class NotificationStore {
  // Private writable signal
  private notificationStateSignal = signal<NotificationState>(DEFAULT_NOTIFICATION_STATE);

  // Public readonly signals
  readonly notificationState = this.notificationStateSignal.asReadonly();
  readonly notifications = computed(() => this.notificationStateSignal().notifications);
  readonly unreadCount = computed(() => this.notificationStateSignal().unreadCount);
  readonly hasUnread = computed(() => this.notificationStateSignal().unreadCount > 0);
  readonly recentNotifications = computed(() => 
    this.notificationStateSignal().notifications.slice(0, 5)
  );

  /**
   * Add notification
   */
  add(
    type: NotificationType,
    title: string,
    message: string,
    options?: {
      duration?: number;
      position?: NotificationPosition;
      actionUrl?: string;
      actionLabel?: string;
    }
  ): string {
    const id = this.generateId();
    const notification: Notification = {
      id,
      type,
      title,
      message,
      duration: options?.duration ?? 3000,
      position: options?.position ?? NotificationPosition.TOP_RIGHT,
      timestamp: new Date(),
      read: false,
      actionUrl: options?.actionUrl,
      actionLabel: options?.actionLabel
    };

    this.notificationStateSignal.update(state => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1
    }));

    // Auto-remove after duration
    if (notification.duration && notification.duration > 0) {
      setTimeout(() => this.remove(id), notification.duration);
    }

    return id;
  }

  /**
   * Success notification
   */
  success(title: string, message: string, options?: any): string {
    return this.add(NotificationType.SUCCESS, title, message, options);
  }

  /**
   * Error notification
   */
  error(title: string, message: string, options?: any): string {
    return this.add(NotificationType.ERROR, title, message, options);
  }

  /**
   * Warning notification
   */
  warning(title: string, message: string, options?: any): string {
    return this.add(NotificationType.WARNING, title, message, options);
  }

  /**
   * Info notification
   */
  info(title: string, message: string, options?: any): string {
    return this.add(NotificationType.INFO, title, message, options);
  }

  /**
   * Remove notification
   */
  remove(id: string): void {
    this.notificationStateSignal.update(state => {
      const notification = state.notifications.find(n => n.id === id);
      const unreadDecrement = notification && !notification.read ? 1 : 0;

      return {
        notifications: state.notifications.filter(n => n.id !== id),
        unreadCount: Math.max(0, state.unreadCount - unreadDecrement)
      };
    });
  }

  /**
   * Mark notification as read
   */
  markAsRead(id: string): void {
    this.notificationStateSignal.update(state => {
      const notification = state.notifications.find(n => n.id === id);
      if (!notification || notification.read) {
        return state;
      }

      return {
        notifications: state.notifications.map(n =>
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      };
    });
  }

  /**
   * Mark all as read
   */
  markAllAsRead(): void {
    this.notificationStateSignal.update(state => ({
      notifications: state.notifications.map(n => ({ ...n, read: true })),
      unreadCount: 0
    }));
  }

  /**
   * Clear all notifications
   */
  clear(): void {
    this.notificationStateSignal.set(DEFAULT_NOTIFICATION_STATE);
  }

  /**
   * Clear read notifications
   */
  clearRead(): void {
    this.notificationStateSignal.update(state => ({
      ...state,
      notifications: state.notifications.filter(n => !n.read)
    }));
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

